import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { isInventoryScope, type BinScope } from './bin-scope';

// ─── Types ───

export interface FilterItem {
  item_id: string;
  description: string;
  brand: string;
  category_code: string;
  category: string;
  sub_category_code: string;
  sub_category: string;
  vendor: string;
  item_type: string | null;
  last_sale_date: string | null;
}

export interface ItemSales {
  item_id: string;
  sales_amount_30d: number;
  qty_sold_30d: number;
  tx_count_30d: number;
  last_sale: string | null;
}

export interface ItemBin {
  item_id: string;
  bin_code: string;
  zone_code: string;
  aisle: number | null;
  qty_base: number;
  /** Bin envanter sınıfı (bkz. ADR-0009); eski yanıtlarda yok. */
  binScope?: BinScope;
}

// A shelf relation = bin from either inventory or bin_assignments
export interface ShelfRelation {
  item_id: string;
  bin_code: string;
  zone_code: string;
  aisle: number | null;
}

export interface FilterState {
  search: string;
  categories: Set<string>;       // category_code values
  subCategories: Set<string>;    // sub_category_code values
  brands: Set<string>;
  vendors: Set<string>;
  zones: Set<string>;            // 'CASHCARRY' | 'LAGER'
  status: 'all' | 'assigned' | 'unassigned' | 'missing';
  salesActivity: 'all' | '1m' | '3m' | '6m' | 'inactive';
  specificItems: Set<string>;    // item_ids pinned as chips
}

export interface FilterCounts {
  categories: Map<string, number>;
  subCategories: Map<string, number>;
  brands: Map<string, number>;
  vendors: Map<string, number>;
}

// Saved filter presets (stored in localStorage)
export interface SavedFilter {
  name: string;
  state: SerializedFilterState;
}

interface SerializedFilterState {
  search: string;
  categories: string[];
  subCategories: string[];
  brands: string[];
  vendors: string[];
  zones: string[];
  status: FilterState['status'];
  salesActivity: FilterState['salesActivity'];
  specificItems: string[];
}

function serializeFilters(f: FilterState): SerializedFilterState {
  return {
    search: f.search,
    categories: [...f.categories],
    subCategories: [...f.subCategories],
    brands: [...f.brands],
    vendors: [...f.vendors],
    zones: [...f.zones],
    status: f.status,
    salesActivity: f.salesActivity,
    specificItems: [...f.specificItems],
  };
}

function deserializeFilters(s: SerializedFilterState): FilterState {
  return {
    search: s.search || '',
    categories: new Set(s.categories || []),
    subCategories: new Set(s.subCategories || []),
    brands: new Set(s.brands || []),
    vendors: new Set(s.vendors || []),
    zones: new Set(s.zones || []),
    status: s.status || 'all',
    salesActivity: s.salesActivity || 'all',
    specificItems: new Set(s.specificItems || []),
  };
}

const SAVED_FILTERS_KEY = 'esl-saved-filters';
function loadSavedFilters(): SavedFilter[] {
  try { return JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY) || '[]'); }
  catch { return []; }
}
function persistSavedFilters(filters: SavedFilter[]) {
  localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(filters));
}

interface FilterContextValue {
  // Data
  allItems: FilterItem[];
  allItemBins: ItemBin[];
  assignedPairs: Set<string>;
  salesMap: Map<string, ItemSales>;
  allCategories: {code: string; description: string; parent_code: string | null}[];
  loaded: boolean;

  // Filter state
  filters: FilterState;
  setSearch: (v: string) => void;
  toggleCategory: (code: string) => void;
  toggleSubCategory: (code: string) => void;
  toggleSubCategories: (codes: string[]) => void;
  toggleBrand: (brand: string) => void;
  toggleVendor: (vendor: string) => void;
  toggleZone: (zone: string) => void;
  setStatus: (v: FilterState['status']) => void;
  setSalesActivity: (v: FilterState['salesActivity']) => void;
  addSpecificItem: (itemId: string) => void;
  removeSpecificItem: (itemId: string) => void;
  resetFilters: () => void;

  // Results
  filteredItemIds: Set<string>;
  filterCounts: FilterCounts;
  isFilterActive: boolean;

  // Panel visibility
  panelOpen: boolean;
  setPanelOpen: (v: boolean) => void;

  // Saved filters
  savedFilters: SavedFilter[];
  saveFilter: (name: string) => void;
  loadFilter: (name: string) => void;
  deleteSavedFilter: (name: string) => void;

  // Right product panel
  productPanelOpen: boolean;
  setProductPanelOpen: (v: boolean) => void;
  selectedItemId: string | null;
  setSelectedItemId: (v: string | null) => void;

  // Helpers — these account for selectedItemId override
  isItemMatch: (itemId: string) => boolean;
  isBinMatch: (binCode: string) => boolean;
  isAisleMatch: (aisle: number) => boolean;

  // Filtered items with bin details (for product panel)
  filteredItemsWithBins: {
    item_id: string; description: string; brand: string;
    category_code: string; category: string;
    sub_category_code: string; sub_category: string;
    vendor: string; status: 'assigned' | 'unassigned' | 'missing' | 'in-stock';
    bins: { bin_code: string; zone_code: string }[];
    assignedBins: string[];
    last_sale: string | null;
  }[];
}

const FilterContext = createContext<FilterContextValue | null>(null);

const EMPTY_FILTERS: FilterState = {
  search: '',
  categories: new Set(),
  subCategories: new Set(),
  brands: new Set(),
  vendors: new Set(),
  zones: new Set(),
  status: 'all',
  salesActivity: 'all',
  specificItems: new Set(),
};

function salesActivityMatch(item: FilterItem, activity: FilterState['salesActivity']): boolean {
  if (activity === 'all') return true;
  const lastSale = item.last_sale_date ? new Date(item.last_sale_date) : null;
  const now = new Date();
  const monthsAgo = (months: number) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - months);
    return d;
  };
  switch (activity) {
    case '1m': return !!lastSale && lastSale >= monthsAgo(1);
    case '3m': return !!lastSale && lastSale >= monthsAgo(3);
    case '6m': return !!lastSale && lastSale >= monthsAgo(6);
    case 'inactive': return !lastSale || lastSale < monthsAgo(6);
  }
}

// Apply all filters EXCEPT the excluded group (for cross-filter counts)
function applyFilters(
  items: FilterItem[],
  filters: FilterState,
  assignedPairs: Set<string>,
  itemBinMap: Map<string, ItemBin[]>,
  itemShelfMap: Map<string, ShelfRelation[]>,
  exclude?: keyof FilterState,
): Set<string> {
  const result = new Set<string>();
  for (const item of items) {
    // Search
    if (exclude !== 'search' && filters.search.length >= 2) {
      const q = filters.search.toLowerCase();
      if (!item.item_id.toLowerCase().includes(q) &&
          !item.description.toLowerCase().includes(q) &&
          !item.brand.toLowerCase().includes(q)) continue;
    }
    // Category
    if (exclude !== 'categories' && filters.categories.size > 0) {
      if (!filters.categories.has(item.category_code)) continue;
    }
    // SubCategory
    if (exclude !== 'subCategories' && filters.subCategories.size > 0) {
      if (!filters.subCategories.has(item.sub_category_code)) continue;
    }
    // Brand
    if (exclude !== 'brands' && filters.brands.size > 0) {
      if (!filters.brands.has(item.brand)) continue;
    }
    // Vendor
    if (exclude !== 'vendors' && filters.vendors.size > 0) {
      if (!filters.vendors.has(item.vendor)) continue;
    }
    // Zone — item matches if at least one shelf relation (inventory or
    // assignment) is in the selected zones. Items without any shelf relation
    // are excluded when a zone filter is active.
    if (exclude !== 'zones' && filters.zones.size > 0) {
      const rels = itemShelfMap.get(item.item_id) || [];
      if (!rels.some(r => filters.zones.has(r.zone_code))) continue;
    }
    // Status
    if (exclude !== 'status' && filters.status !== 'all') {
      const bins = itemBinMap.get(item.item_id) || [];
      const isAssigned = bins.some(b => assignedPairs.has(`${b.bin_code}:${item.item_id}`));
      // "Stokta" yalnızca Envanter Bin'lerinden hesaplanır: yalnızca TEMP gibi
      // bir Envanter Dışı Bin'de duran ürün stokta sayılmaz (bkz. ADR-0009).
      const hasStock = bins.some(b => isInventoryScope(b.binScope));
      if (filters.status === 'assigned' && !isAssigned) continue;
      if (filters.status === 'unassigned' && isAssigned) continue;
      if (filters.status === 'missing' && !(isAssigned && !hasStock)) continue;
    }
    // Sales activity
    if (exclude !== 'salesActivity' && filters.salesActivity !== 'all') {
      if (!salesActivityMatch(item, filters.salesActivity)) continue;
    }
    // Specific items
    if (exclude !== 'specificItems' && filters.specificItems.size > 0) {
      if (!filters.specificItems.has(item.item_id)) continue;
    }
    result.add(item.item_id);
  }
  return result;
}

export function FilterProvider({ children }: { children: ReactNode }) {
  const [allItems, setAllItems] = useState<FilterItem[]>([]);
  const [allItemBins, setAllItemBins] = useState<ItemBin[]>([]);
  const [assignmentBins, setAssignmentBins] = useState<ShelfRelation[]>([]);
  const [assignedPairs, setAssignedPairs] = useState<Set<string>>(new Set());
  const [salesMap, setSalesMap] = useState<Map<string, ItemSales>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ ...EMPTY_FILTERS });
  const [panelOpen, setPanelOpen] = useState(false);
  const [productPanelOpen, setProductPanelOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(loadSavedFilters);
  const [allCategories, setAllCategories] = useState<{code: string; description: string; parent_code: string | null}[]>([]);

  // Load all items on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://esl-api.megafood.dk/api/items/all');
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setAllItems(data.items);
        setAllItemBins(data.itemBins);
        const pairs = new Set<string>();
        const assigns: ShelfRelation[] = [];
        for (const a of data.assignments) {
          pairs.add(`${a.bin_code}:${a.item_id}`);
          assigns.push({
            item_id: a.item_id,
            bin_code: a.bin_code,
            zone_code: a.zone_code || '',
            aisle: a.aisle ?? null,
          });
        }
        setAssignedPairs(pairs);
        setAssignmentBins(assigns);
        const sm = new Map<string, ItemSales>();
        if (data.sales) {
          for (const s of data.sales) sm.set(s.item_id, s);
        }
        setSalesMap(sm);
        setAllCategories(data.categories || []);
        setLoaded(true);
      } catch (err) { console.error('Failed to load filter data:', err); }
    })();
  }, []);

  // Build item->bins lookup (inventory only — used for has-stock semantics)
  const itemBinMap = useMemo(() => {
    const m = new Map<string, ItemBin[]>();
    for (const ib of allItemBins) {
      if (!m.has(ib.item_id)) m.set(ib.item_id, []);
      m.get(ib.item_id)!.push(ib);
    }
    return m;
  }, [allItemBins]);

  // Unified shelf relations: inventory ∪ assignments, deduped by item+bin.
  // Used for zone/aisle/bin gating — covers all three cases:
  //   (a) assigned with inventory, (b) assigned without inventory,
  //   (c) inventory without assignment.
  const itemShelfMap = useMemo(() => {
    const m = new Map<string, ShelfRelation[]>();
    const seen = new Set<string>();
    const add = (r: ShelfRelation) => {
      const k = `${r.item_id}::${r.bin_code}`;
      if (seen.has(k)) return;
      seen.add(k);
      if (!m.has(r.item_id)) m.set(r.item_id, []);
      m.get(r.item_id)!.push(r);
    };
    for (const ib of allItemBins) add({ item_id: ib.item_id, bin_code: ib.bin_code, zone_code: ib.zone_code, aisle: ib.aisle });
    for (const a of assignmentBins) add(a);
    return m;
  }, [allItemBins, assignmentBins]);

  // Build bin->items lookup (union of inventory + assignments)
  const binItemMap = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const [itemId, rels] of itemShelfMap) {
      for (const r of rels) {
        if (!m.has(r.bin_code)) m.set(r.bin_code, new Set());
        m.get(r.bin_code)!.add(itemId);
      }
    }
    return m;
  }, [itemShelfMap]);

  // Aisle->bins (bin -> zone) and bin->zone lookups, built from the unified
  // shelf-relations (inventory + assignments).
  const aisleBinsMap = useMemo(() => {
    const m = new Map<number, Map<string, string>>();
    for (const rels of itemShelfMap.values()) {
      for (const r of rels) {
        if (r.aisle == null) continue;
        if (!m.has(r.aisle)) m.set(r.aisle, new Map());
        m.get(r.aisle)!.set(r.bin_code, r.zone_code || '');
      }
    }
    return m;
  }, [itemShelfMap]);

  const binZoneMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const rels of itemShelfMap.values()) {
      for (const r of rels) {
        if (r.zone_code) m.set(r.bin_code, r.zone_code);
      }
    }
    return m;
  }, [itemShelfMap]);

  // Filtered item IDs
  const filteredItemIds = useMemo(
    () => applyFilters(allItems, filters, assignedPairs, itemBinMap, itemShelfMap),
    [allItems, filters, assignedPairs, itemBinMap, itemShelfMap],
  );

  // Cross-filter counts
  const filterCounts = useMemo((): FilterCounts => {
    const catItems = applyFilters(allItems, filters, assignedPairs, itemBinMap, itemShelfMap, 'categories');
    const brandItems = applyFilters(allItems, filters, assignedPairs, itemBinMap, itemShelfMap, 'brands');
    const vendorItems = applyFilters(allItems, filters, assignedPairs, itemBinMap, itemShelfMap, 'vendors');
    const subCatItems = applyFilters(allItems, filters, assignedPairs, itemBinMap, itemShelfMap, 'subCategories');

    const categories = new Map<string, number>();
    const subCategories = new Map<string, number>();
    const brands = new Map<string, number>();
    const vendors = new Map<string, number>();

    for (const item of allItems) {
      if (catItems.has(item.item_id) && item.category_code) {
        categories.set(item.category_code, (categories.get(item.category_code) || 0) + 1);
      }
      if (subCatItems.has(item.item_id) && item.sub_category_code) {
        subCategories.set(item.sub_category_code, (subCategories.get(item.sub_category_code) || 0) + 1);
      }
      if (brandItems.has(item.item_id) && item.brand) {
        brands.set(item.brand, (brands.get(item.brand) || 0) + 1);
      }
      if (vendorItems.has(item.item_id) && item.vendor) {
        vendors.set(item.vendor, (vendors.get(item.vendor) || 0) + 1);
      }
    }

    return { categories, subCategories, brands, vendors };
  }, [allItems, filters, assignedPairs, itemBinMap, itemShelfMap]);

  const isFilterActive = useMemo(() =>
    filters.search.length >= 2 || filters.categories.size > 0 || filters.subCategories.size > 0 ||
    filters.brands.size > 0 || filters.vendors.size > 0 || filters.zones.size > 0 ||
    filters.status !== 'all' ||
    filters.salesActivity !== 'all' || filters.specificItems.size > 0,
  [filters]);

  // Toggle helpers
  const toggle = (key: 'categories' | 'subCategories' | 'brands' | 'vendors' | 'zones', value: string) => {
    setFilters(prev => {
      const next = new Set(prev[key]);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...prev, [key]: next };
    });
  };

  const saveFilter = useCallback((name: string) => {
    const serialized = serializeFilters(filters);
    setSavedFilters(prev => {
      const next = prev.filter(f => f.name !== name);
      next.push({ name, state: serialized });
      persistSavedFilters(next);
      return next;
    });
  }, [filters]);

  const loadFilter = useCallback((name: string) => {
    const found = savedFilters.find(f => f.name === name);
    if (found) setFilters(deserializeFilters(found.state));
  }, [savedFilters]);

  const deleteSavedFilter = useCallback((name: string) => {
    setSavedFilters(prev => {
      const next = prev.filter(f => f.name !== name);
      persistSavedFilters(next);
      return next;
    });
  }, []);

  // Effective filter: if a product is selected in the right panel, it overrides
  const effectiveItemIds = useMemo(() => {
    if (selectedItemId && filteredItemIds.has(selectedItemId)) {
      return new Set([selectedItemId]);
    }
    return filteredItemIds;
  }, [selectedItemId, filteredItemIds]);

  const isEffectiveFilterActive = isFilterActive || !!selectedItemId;

  // Filtered items with bin details for the product panel
  const filteredItemsWithBins = useMemo(() => {
    const result: FilterContextValue['filteredItemsWithBins'] = [];
    for (const item of allItems) {
      if (!filteredItemIds.has(item.item_id)) continue;
      const itemBins = itemBinMap.get(item.item_id) || [];
      const bins = itemBins.map(b => ({ bin_code: b.bin_code, zone_code: b.zone_code }));
      const assignedBins: string[] = [];
      for (const b of bins) {
        if (assignedPairs.has(`${b.bin_code}:${item.item_id}`)) assignedBins.push(b.bin_code);
      }
      // "Stokta" yalnızca Envanter Bin'lerinden (bkz. ADR-0009).
      const hasStock = itemBins.some(b => isInventoryScope(b.binScope));
      const isAssigned = assignedBins.length > 0;
      let status: 'assigned' | 'unassigned' | 'missing' | 'in-stock';
      if (isAssigned && hasStock) status = 'assigned';
      else if (isAssigned && !hasStock) status = 'missing';
      else if (!isAssigned && hasStock) status = 'in-stock';
      else status = 'unassigned';
      const sale = salesMap.get(item.item_id);
      result.push({
        item_id: item.item_id, description: item.description, brand: item.brand,
        category_code: item.category_code, category: item.category,
        sub_category_code: item.sub_category_code, sub_category: item.sub_category,
        vendor: item.vendor,
        status, bins, assignedBins,
        last_sale: sale?.last_sale || null,
      });
    }
    return result;
  }, [allItems, filteredItemIds, itemBinMap, assignedPairs, salesMap]);

  const ctx: FilterContextValue = {
    allItems, allItemBins, assignedPairs, salesMap, allCategories, loaded,
    filters,
    setSearch: v => setFilters(f => ({ ...f, search: v })),
    toggleCategory: v => toggle('categories', v),
    toggleSubCategory: v => toggle('subCategories', v),
    toggleSubCategories: (codes: string[]) => {
      setFilters(prev => {
        const next = new Set(prev.subCategories);
        const allSelected = codes.every(c => next.has(c));
        if (allSelected) {
          codes.forEach(c => next.delete(c));
        } else {
          codes.forEach(c => next.add(c));
        }
        return { ...prev, subCategories: next };
      });
    },
    toggleBrand: v => toggle('brands', v),
    toggleVendor: v => toggle('vendors', v),
    toggleZone: v => toggle('zones', v),
    setStatus: v => setFilters(f => ({ ...f, status: v })),
    setSalesActivity: v => setFilters(f => ({ ...f, salesActivity: v })),
    addSpecificItem: v => setFilters(f => ({ ...f, specificItems: new Set([...f.specificItems, v]) })),
    removeSpecificItem: v => setFilters(f => {
      const next = new Set(f.specificItems);
      next.delete(v);
      return { ...f, specificItems: next };
    }),
    resetFilters: () => { setFilters({ ...EMPTY_FILTERS, categories: new Set(), subCategories: new Set(), brands: new Set(), vendors: new Set(), zones: new Set(), specificItems: new Set() }); setSelectedItemId(null); },
    filteredItemIds, filterCounts, isFilterActive,
    panelOpen, setPanelOpen,
    productPanelOpen, setProductPanelOpen,
    selectedItemId, setSelectedItemId,
    savedFilters, saveFilter, loadFilter, deleteSavedFilter,
    filteredItemsWithBins,
    isItemMatch: useCallback((itemId: string) => effectiveItemIds.has(itemId), [effectiveItemIds]),
    isBinMatch: useCallback((binCode: string) => {
      if (!isEffectiveFilterActive) return true;
      // Zone gate: bin's own zone must be in selected zones
      if (filters.zones.size > 0) {
        const z = binZoneMap.get(binCode);
        if (!z || !filters.zones.has(z)) return false;
      }
      const items = binItemMap.get(binCode);
      if (!items) return false;
      return Array.from(items).some(id => effectiveItemIds.has(id));
    }, [isEffectiveFilterActive, binItemMap, effectiveItemIds, filters.zones, binZoneMap]),
    isAisleMatch: useCallback((aisle: number) => {
      if (!isEffectiveFilterActive) return true;
      // Bin-level match: aisle matches if at least one of its bins both
      //  (a) passes the zone gate, and
      //  (b) contains an item from the effective filtered set.
      const bins = aisleBinsMap.get(aisle);
      if (!bins) return false;
      const zoneActive = filters.zones.size > 0;
      for (const [binCode, zone] of bins) {
        if (zoneActive && !filters.zones.has(zone)) continue;
        const items = binItemMap.get(binCode);
        if (!items) continue;
        for (const id of items) {
          if (effectiveItemIds.has(id)) return true;
        }
      }
      return false;
    }, [isEffectiveFilterActive, aisleBinsMap, binItemMap, effectiveItemIds, filters.zones]),
  };

  return <FilterContext.Provider value={ctx}>{children}</FilterContext.Provider>;
}

export function useFilter() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilter must be used within FilterProvider');
  return ctx;
}
